import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

import { SousService } from 'app/models/sous-service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import { AgenciesService } from 'app/services/agencies/agencies.service';

type ImageUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-modifier-sous-service',
  templateUrl: './modifier-sous-service.component.html',
  styleUrls: ['./modifier-sous-service.component.scss']
})
export class ModifierSousServiceComponent implements OnInit {
  // Sous-service data
  sousService: SousService = new SousService();
  availableCategories: string[] = [];
  availableCategoriesArab: string[] = [];
  availableCategoriesAnglais: string[] = [];
  agencyAvailableLanguages: string[] = ['FR'];

  idSousService: string | null = null;
  parentServiceId: string | null = null;

  // Images management
  existingImages: string[] = [];
  newImages: string[] = [];
  deletedImages: string[] = [];

  // ==================== MAIN IMAGE ====================
  mainImageFile: File | null = null;
  isUploadingMainImage: boolean = false;

  mainImageUploadMode: ImageUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;

  // ==================== GALLERY ====================
  selectedFiles: FileList | null = null;
  isUploadingGallery: boolean = false;

  galleryUploadMode: ImageUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;

  // Form state
  isSubmitting: boolean = false;

  // ViewChild references
  @ViewChild('mainImageInput') mainImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryFileInput') galleryFileInput!: ElementRef<HTMLInputElement>;

  // ============================================================
  // ✅ RÉFÉRENCES AUX ÉDITEURS contenteditable (WYSIWYG)
  // ============================================================
  @ViewChild('descFrEditor') descFrEditor?: ElementRef<HTMLElement>;
  @ViewChild('descArEditor') descArEditor?: ElementRef<HTMLElement>;
  @ViewChild('descEnEditor') descEnEditor?: ElementRef<HTMLElement>;
  // ============================================================

  constructor(
    private sousServiceService: SousServiceService,
    private awsService: AwscontrolService,
    private agenciesService: AgenciesService,
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.idSousService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadSousServiceDetails();

    this.sousServiceService.refreshNedeed.subscribe(() => {
      this.loadSousServiceDetails();
    });

    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => console.log('✅ AWS S3 Connection Healthy:', response),
      error: (err) => console.warn('⚠️ AWS S3 Connection Warning:', err)
    });
  }

  loadSousServiceDetails(): void {
    if (this.idSousService) {
      this.sousServiceService.getSousService(this.idSousService).subscribe({
        next: (data: any) => {
          this.sousService = data;

          if (data.service?.id) {
            this.parentServiceId = data.service.id.toString();
          } else if (data.serviceId) {
            this.parentServiceId = data.serviceId.toString();
          }

          if (!this.sousService.image_url) {
            this.sousService.image_url = '';
          }
          this.existingImages = data.listimages || [];
          this.newImages = [];
          this.deletedImages = [];

          if (data.service) {
            this.availableCategories = data.service.categoryList || [];
            this.availableCategoriesArab = data.service.categoryListarab || [];
            this.availableCategoriesAnglais = data.service.categoryListanglais || [];

            if (data.service.userId) {
              this.loadAgencyLanguages(data.service.userId);
            } else {
              this.agencyAvailableLanguages = ['FR'];
            }
          } else {
            this.availableCategories = this.sousService.category ? [this.sousService.category] : [];
            this.availableCategoriesArab = this.sousService.categoryarab ? [this.sousService.categoryarab] : [];
            this.availableCategoriesAnglais = this.sousService.categoryanglais ? [this.sousService.categoryanglais] : [];
            this.agencyAvailableLanguages = ['FR'];
          }

          // Load Arabic fields
          this.sousService.namearabe = data.namearabe || '';
          this.sousService.categoryarab = data.categoryarab || '';
          this.sousService.descriptionarabe = data.descriptionarabe || '';

          // Load English fields
          this.sousService.nameanglais = data.nameanglais || '';
          this.sousService.categoryanglais = data.categoryanglais || '';
          this.sousService.descriptionanglais = data.descriptionanglais || '';

          // Load video URL
          this.sousService.video = data.video || '';

          // 🔥 Remplir les éditeurs après rendu
          this.applyRichTextAfterRender();
        },
        error: (error) => {
          console.error('Error loading sous-service:', error);
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: 'Impossible de charger les détails du sous-service',
            confirmButtonColor: '#f44336'
          });
        }
      });
    }
  }

  private loadAgencyLanguages(agencyId: number): void {
    this.agenciesService.getAgencieById(agencyId).subscribe({
      next: (agency: any) => {
        this.agencyAvailableLanguages = agency.availableLanguages || ['FR'];
        if (this.agencyAvailableLanguages.length === 0) {
          this.agencyAvailableLanguages = ['FR'];
        }
        this.applyRichTextAfterRender();
      },
      error: (err) => {
        console.warn('Could not load agency languages, using FR only:', err);
        this.agencyAvailableLanguages = ['FR'];
      }
    });
  }

  /* ============================================================
     ✅ SYNC DES ÉDITEURS contenteditable
     ============================================================ */
  private applyRichTextAfterRender(): void {
    setTimeout(() => this.syncEditors(), 0);
    setTimeout(() => this.syncEditors(), 100);
    setTimeout(() => this.syncEditors(), 300);
  }

  private syncEditors(): void {
    if (this.descFrEditor) {
      const html = this.sousService.description || '';
      if (this.descFrEditor.nativeElement.innerHTML !== html) {
        this.descFrEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descArEditor) {
      const html = this.sousService.descriptionarabe || '';
      if (this.descArEditor.nativeElement.innerHTML !== html) {
        this.descArEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descEnEditor) {
      const html = this.sousService.descriptionanglais || '';
      if (this.descEnEditor.nativeElement.innerHTML !== html) {
        this.descEnEditor.nativeElement.innerHTML = html;
      }
    }
  }

  /* ============================================================
     ✅ HANDLERS (input) DES CONTENTEDITABLE
     ============================================================ */
  onDescFrInput(): void {
    if (this.descFrEditor) {
      this.sousService.description = this.descFrEditor.nativeElement.innerHTML;
    }
  }

  onDescArInput(): void {
    if (this.descArEditor) {
      this.sousService.descriptionarabe = this.descArEditor.nativeElement.innerHTML;
    }
  }

  onDescEnInput(): void {
    if (this.descEnEditor) {
      this.sousService.descriptionanglais = this.descEnEditor.nativeElement.innerHTML;
    }
  }

  /* ============================================================
     BOLD — seule action de rich text exposée
     ============================================================ */
  makeBold(): void {
    try {
      document.execCommand('bold', false);
    } catch (e) {
      console.warn('execCommand("bold") failed:', e);
    }
  }

  // ==================== VALIDATION UTILITAIRE ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'La taille maximale autorisée est de 5MB', confirmButtonColor: '#ff9800' });
      return false;
    }
    return true;
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: ImageUploadMode): void {
    if (this.isUploadingMainImage) return;
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview(true);
    }
    this.mainImageUploadMode = mode;
    console.log('🔄 Mode image principale :', mode);
  }

  triggerMainImageUpload(): void {
    if (!this.isUploadingMainImage && !this.isAwaitingMainValidation) {
      this.mainImageInput.nativeElement.click();
    }
  }

  onMainImageSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) {
      target.value = '';
      return;
    }
    this.mainImageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.mainPreviewOriginalUrl = (e.target?.result as string) || null;
    };
    reader.readAsDataURL(file);

    if (this.mainImageUploadMode === 'direct') {
      this.uploadMainImage();
    } else {
      this.uploadMainImageViaImagesousproduit();
    }
  }

  uploadMainImage(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMainImage = true;

    this.awsService.uploadFiles([this.mainImageFile], 'sous-service-main-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploadingMainImage = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.sousService.image_url = response.urls[0];
          this.mainPreviewOriginalUrl = null;
          Swal.fire({
            icon: 'success',
            title: 'Image principale téléchargée',
            text: 'L\'image de couverture a été mise à jour',
            confirmButtonColor: '#4caf50',
            timer: 1500,
            showConfirmButton: false
          });
          if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
          this.mainImageFile = null;
        } else {
          Swal.fire('Erreur', response.error || 'Échec du téléchargement', 'error');
        }
      },
      error: (err) => {
        this.isUploadingMainImage = false;
        console.error('Main image upload error:', err);
        Swal.fire('Erreur', 'Impossible de télécharger l\'image principale', 'error');
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  uploadMainImageViaImagesousproduit(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMainImage = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([this.mainImageFile], 'sous-service-main-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingMainImage = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire('Erreur', (response.errors && response.errors[0]) || 'Échec du traitement', 'error');
          this.mainPreviewOriginalUrl = null;
        }
      },
      error: () => {
        this.isUploadingMainImage = false;
        Swal.fire('Erreur', 'Impossible de traiter l\'image principale', 'error');
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  validateMainImagePreview(): void {
    if (!this.mainPreviewProcessedUrl) return;

    this.sousService.image_url = this.mainPreviewProcessedUrl;

    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    this.mainImageFile = null;

    if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'L\'image principale a été acceptée.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelMainImagePreview(silent: boolean = false): void {
    const urlToDelete = this.mainPreviewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image principale refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
      this.mainImageFile = null;

      if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: 'L\'image traitée sera supprimée et vous pourrez en choisir une autre.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview();
      return;
    }

    const oldImageUrl = this.sousService.image_url;
    if (!oldImageUrl) {
      this.sousService.image_url = '';
      return;
    }
    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(oldImageUrl).subscribe({
          next: () => {
            this.sousService.image_url = '';
            Swal.fire({
              icon: 'success',
              title: 'Supprimée',
              text: 'L\'image principale a été supprimée',
              timer: 1500,
              showConfirmButton: false
            });
          },
          error: (err) => {
            console.error('Delete error:', err);
            Swal.fire('Erreur', 'Impossible de supprimer l\'image du serveur', 'error');
          }
        });
      }
    });
  }

  // ============================================================
  // ==================== GALLERY ===============================
  // ============================================================

  setGalleryUploadMode(mode: ImageUploadMode): void {
    if (this.isUploadingGallery) return;
    if (this.isAwaitingGalleryValidation) {
      this.cancelGalleryPreview(true);
    }
    this.galleryUploadMode = mode;
    console.log('🔄 Mode galerie :', mode);
  }

  triggerGalleryUpload(): void {
    if (!this.isUploadingGallery && !this.isAwaitingGalleryValidation) {
      this.galleryFileInput.nativeElement.click();
    }
  }

  onGalleryFilesSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (this.validateImageFile(files[i])) {
        validFiles.push(files[i]);
      }
    }

    if (validFiles.length === 0) {
      target.value = '';
      return;
    }

    this.selectedFiles = target.files;

    this.galleryPreviewOriginalUrls = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = (e.target?.result as string) || '';
        if (url) this.galleryPreviewOriginalUrls.push(url);
      };
      reader.readAsDataURL(file);
    });

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryImages(validFiles);
    } else {
      this.uploadGalleryImagesViaImagesousproduit(validFiles);
    }

    target.value = '';
  }

  uploadGalleryImages(files: File[]): void {
    if (!files || files.length === 0) return;
    this.isUploadingGallery = true;

    this.awsService.uploadFiles(files, 'sous-service-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.newImages.push(...response.urls);
          this.galleryPreviewOriginalUrls = [];
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.count} image(s) ont été téléchargée(s)`,
            confirmButtonColor: '#4caf50',
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire('Erreur', response.error || 'Échec du téléchargement', 'error');
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: (err) => {
        this.isUploadingGallery = false;
        console.error('Upload error:', err);
        Swal.fire('Erreur', 'Impossible de télécharger les images', 'error');
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  uploadGalleryImagesViaImagesousproduit(files: File[]): void {
    if (!files || files.length === 0) return;
    this.isUploadingGallery = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'sous-service-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire('Erreur', (response.errors && response.errors[0]) || 'Échec du traitement', 'error');
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: () => {
        this.isUploadingGallery = false;
        Swal.fire('Erreur', 'Impossible de traiter les images de la galerie', 'error');
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  validateGalleryPreview(): void {
    if (!this.galleryPreviewProcessedUrls.length) return;

    this.newImages.push(...this.galleryPreviewProcessedUrls);

    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    this.selectedFiles = null;

    if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';

    Swal.fire({
      icon: 'success',
      title: 'Images validées',
      text: `${this.newImages.length} nouvelle(s) image(s) prête(s) à être enregistrée(s).`,
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urlsToDelete = [...this.galleryPreviewProcessedUrls];

    const doCancel = () => {
      urlsToDelete.forEach(url => {
        this.awsService.deleteFile(url).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      });
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
      this.selectedFiles = null;

      if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser ces images ?',
      text: 'Les images traitées seront supprimées et vous pourrez en choisir d\'autres.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  deleteExistingImage(index: number): void {
    const imageUrl = this.existingImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: () => {
            this.existingImages.splice(index, 1);
            this.deletedImages.push(imageUrl);
            Swal.fire({ icon: 'success', title: 'Supprimée', text: 'L\'image a été supprimée', timer: 1500, showConfirmButton: false });
          },
          error: (err) => {
            console.error('Delete error:', err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de supprimer l\'image du serveur', confirmButtonColor: '#f44336' });
          }
        });
      }
    });
  }

  deleteNewImage(index: number): void {
    const imageUrl = this.newImages[index];
    Swal.fire({
      title: 'Supprimer cette nouvelle image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: () => {
            this.newImages.splice(index, 1);
            Swal.fire({ icon: 'success', title: 'Supprimée', text: 'L\'image a été supprimée', timer: 1500, showConfirmButton: false });
          },
          error: (err) => {
            console.error('Delete error:', err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de supprimer l\'image', confirmButtonColor: '#f44336' });
          }
        });
      }
    });
  }

  // ==================== SUBMIT ====================
  updateSousService(): void {
    if (this.isAwaitingMainValidation) {
      Swal.fire('Validation requise', 'Veuillez valider ou refuser l\'image principale.', 'warning');
      return;
    }
    if (this.isAwaitingGalleryValidation) {
      Swal.fire('Validation requise', 'Veuillez valider ou refuser les images de la galerie.', 'warning');
      return;
    }

    if (!this.sousService.name || this.sousService.name.trim() === '') {
      Swal.fire({ icon: 'warning', title: 'Nom requis', text: 'Veuillez saisir un nom', confirmButtonColor: '#ff9800' });
      return;
    }

    // 🔥 Récupérer les valeurs depuis les éditeurs contenteditable
    this.onDescFrInput();
    this.onDescArInput();
    this.onDescEnInput();

    this.isSubmitting = true;

    const finalImageList = [...this.existingImages, ...this.newImages];
    const sousServiceData = {
      name: this.sousService.name.trim(),
      namearabe: this.sousService.namearabe?.trim() || '',
      nameanglais: this.sousService.nameanglais?.trim() || '',
      category: this.sousService.category?.trim() || '',
      categoryarab: this.sousService.categoryarab?.trim() || '',
      categoryanglais: this.sousService.categoryanglais?.trim() || '',
      description: this.sousService.description || '',
      descriptionarabe: this.sousService.descriptionarabe || '',
      descriptionanglais: this.sousService.descriptionanglais || '',
      prix: this.sousService.prix?.trim() || '',
      video: this.sousService.video?.trim() || '',
      image_url: this.sousService.image_url?.trim() || '',
      listimages: finalImageList
    };

    if (!this.idSousService) {
      Swal.fire({ icon: 'error', title: 'ID manquant', text: 'Impossible de mettre à jour sans ID', confirmButtonColor: '#f44336' });
      this.isSubmitting = false;
      return;
    }

    this.sousServiceService.updateSousService(this.idSousService, sousServiceData).subscribe({
      next: () => {
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: 'Sous-service mis à jour avec succès',
          confirmButtonColor: '#4caf50',
          timer: 2000,
          showConfirmButton: false
        }).then(() => this.goToParentService());
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la mise à jour';
    if (error.status === 400) errorMessage = 'Données invalides';
    else if (error.status === 401) errorMessage = 'Session expirée, veuillez vous reconnecter';
    else if (error.status === 403) errorMessage = 'Permissions insuffisantes';
    else if (error.status === 404) errorMessage = 'Sous-service introuvable';
    else if (error.status === 500) errorMessage = 'Erreur interne du serveur';
    Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
  }

  // ==================== NAVIGATION ====================
  private goToParentService(): void {
    if (this.parentServiceId) {
      this.router.navigate(['/list-Sous-Services', this.parentServiceId]);
    } else {
      this.location.back();
    }
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications ?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadSousServiceDetails();
        this.newImages = [];
        this.deletedImages = [];

        this.mainPreviewOriginalUrl = null;
        this.mainPreviewProcessedUrl = null;
        this.isAwaitingMainValidation = false;
        this.mainImageUploadMode = 'direct';

        this.galleryPreviewOriginalUrls = [];
        this.galleryPreviewProcessedUrls = [];
        this.isAwaitingGalleryValidation = false;
        this.galleryUploadMode = 'direct';

        if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
      }
    });
  }

  navigateBack(): void {
    this.goToParentService();
  }

  cancel(): void {
    this.goToParentService();
  }

  getFileNameFromUrl(url: string): string {
    return url.substring(url.lastIndexOf('/') + 1);
  }
}